import type { HTMLAttributes, ReactNode } from 'react';
import { useContext } from 'react';

import CMSLink from '@tmh-website/components/shared/CMSLink';
import { getHomepagelink } from '@tmh-website/services/link.service';
import { GlobalContext, PageContext } from '@tmh-website/shared';
import { LinkField } from '@tmh-website/shared/cms';
import { Audience, LocalePrefix } from '@tmh-website/shared/const';

import * as Styled from './AudienceTab.styles';
import { AudienceTabThemeColor } from './AudienceTab.theme';

interface AudienceTabProps extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  children: ReactNode;
  onClick?: () => void;
  themeColor: AudienceTabThemeColor;
  isChecked?: boolean;
  isStandaloneMode: boolean;
  /**
   * This has to be set if you want to use this component for switching audiences.
   */
  targetAudience?: Audience;
  locale: LocalePrefix;
  href?: string;
  hasMargin?: boolean;
  inMenu: boolean;
}

const AudienceTab = ({
  themeColor,
  isChecked,
  isStandaloneMode,
  targetAudience,
  onClick,
  children,
  locale,
  href,
  hasMargin = true,
  inMenu,
  ...props
}: AudienceTabProps) => {
  const { homepageLinks } = useContext(GlobalContext);
  const page = useContext(PageContext);
  const newHomepageLink = getHomepagelink(homepageLinks!, locale, targetAudience!);
  const isHomepageLink = targetAudience && !page.audiences.includes(targetAudience!);

  const Tab = (
    <Styled.Container
      as={isHomepageLink || href ? 'a' : 'button'}
      $isChecked={!!isChecked}
      $themeColor={themeColor}
      onClick={onClick}
      href={href}
      $hasMargin={hasMargin}
      data-cy-audience={targetAudience}
      data-cy-selected={isChecked}
      $inMenu={inMenu}
      {...props}>
      {children}
    </Styled.Container>
  );

  if (isHomepageLink) {
    return (
      <CMSLink link={{ value: newHomepageLink } as LinkField} isStandaloneMode={isStandaloneMode} locale={locale}>
        {Tab}
      </CMSLink>
    );
  }

  return Tab;
};

export default AudienceTab;
